import { useEffect } from 'react';

import { useIntl } from 'react-intl';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import {
	AnchorPageRelayQuery,
	shapeAnchorContentfulResponse,
} from 'src/contentful/graphql/anchor-page';

import { anchorPageRelayQuery } from '@/__generated_contentful__/anchorPageRelayQuery.graphql';
import { CarouselProps } from '@/components/confluence/carousel/src/carousel';
import { useLogMeInD0to6 } from '@/components/confluence/hooks/use-log-me-in-d0to6.tsx';
import HeroSection from '@/templates/software/confluence/confluence-software-page/src/sections/log-me-in/hero';
import {
	AnchorContentsResponse,
	PaidLandingPersonaTypes,
} from '@/utils/anchor-queries/get-anchor-contents';
import { experiments } from '@/utils/experiment/manifest';
import { useExperimentExposure } from '@/utils/experiment/use-experiment-exposure';
import { FeatureExposed } from '@/utils/feature-exposed/use-feature-exposed';

import { ConfluenceSoftwarePageProps } from './confluence-software-page';
import { carouselHeroMessages } from './messages/carousel-hero.messages';
import { ConfluencePagePaidLpConflAnchor } from './paid-lp-confl-anchor';
import { isTargetedSubAnchor } from './subAnchors/subAnchor-util.tsx';

export interface ConfluenceSoftwarePageAnchorCustomerServiceRelayProps extends ConfluenceSoftwarePageProps {
	anchorContents?: AnchorContentsResponse | null;
	persona?: string;
	queryRef?: PreloadedQuery<anchorPageRelayQuery>;
}
export const ConfluenceSoftwarePageAnchorCustomerService = (
	pageProps: ConfluenceSoftwarePageAnchorCustomerServiceRelayProps,
) => {
	const intl = useIntl();
	const [exposureSubAnchorFired, fireSubAnchorExposure] = useExperimentExposure(
		experiments.cfind4782LandingPageSubAnchorV3,
	);
	useEffect(() => {
		if (isTargetedSubAnchor(pageProps.subAnchor) && !exposureSubAnchorFired) {
			fireSubAnchorExposure();
		}
	}, [exposureSubAnchorFired, fireSubAnchorExposure]);

	const data = usePreloadedQuery(
		AnchorPageRelayQuery,
		pageProps.queryRef || (null as unknown as PreloadedQuery<anchorPageRelayQuery>),
	);
	pageProps = Object.assign({}, pageProps, {
		anchorContents: shapeAnchorContentfulResponse(data),
	});

	const { userProfileName, recentSites, shouldShowLogMeIn } = useLogMeInD0to6({
		availableProducts: pageProps.availableProducts,
		userProfile: pageProps.userProfile,
	});

	// TODO: add missing images
	const carouselSlides = [
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.CustomerService.dynamic-documentation.title',
				defaultMessage: 'Dynamic documentation',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.CustomerService.dynamic-documentation.body',
				defaultMessage:
					'Brainstorm ideas and workflows, document best practices, and share knowledge—all in one place.',
			}),
			imgSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/1e81unagi717g13fj44qtgpk0h8r0537.webp',
			imgAltText: 'A Confluence page being edited',
		},
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.CustomerService.scale-practices.title',
				defaultMessage: 'Scale best practices across your org',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.CustomerService.scale-practices.body',
				defaultMessage:
					'Boost productivity by using Automation to coordinate workflows, prompting next steps when a dependency is completed.',
			}),
			imgSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/k2it51f6l70x62mw505kfe305m744wnv.webp',
			imgAltText: 'A Confluence automation rule',
		},
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.CustomerService.connect-your-work.title',
				defaultMessage: 'Centralize and connect all your work',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.CustomerService.connect-your-work.body',
				defaultMessage:
					'Keep FAQs and commonly referenced documentation organized and easily findable in dedicated workspaces.',
			}),
			imgSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/23124865qxudxy6cj8rl3g6my5h472d7.webp',
			imgAltText: 'Organized Customer Success Hub in Confluence',
		},
	];

	const carouselHeroProps: CarouselProps = {
		'aria-label': intl.formatMessage(carouselHeroMessages.ariaLabel),
		id: 'confluence-customer-service-carousel',
		slides: carouselSlides,
	};

	const pageId = 'anchor-lp-conf-team-customerservice';

	let customHeroSection = null;
	if (shouldShowLogMeIn) {
		customHeroSection = (
			<FeatureExposed experimentKey="cfind-2801-get-paid-users-back-to-product" group="Test">
				<HeroSection
					confluenceSites={recentSites}
					userProfileName={userProfileName}
					hideExploreMore
					loggedInPaid
				/>
			</FeatureExposed>
		);
	}

	return (
		<ConfluencePagePaidLpConflAnchor
			{...pageProps}
			pageId={pageId}
			persona={'unknown' as PaidLandingPersonaTypes}
			pageVariant="customerService"
			dataProductKey="confluence-AnchorLpConfCustomerService"
			carouselHeroProps={carouselHeroProps}
			keyword={pageProps.keyword}
			customHeroSection={customHeroSection}
		/>
	);
};
